import React from "react"
import { Container, Row, Col, Table, Spinner, Button, } from "react-bootstrap"
import { BehaviorSubject, Subscription, concat, of } from "rxjs"
import { catchError, map, switchMap } from "rxjs/operators"
import { toast } from "react-toastify"
import { FaPrint } from "react-icons/fa"
import FileSaver from "file-saver"
import RandomPersonnelGridRecord from "../../api/Randoms/RandomPersonnelModel/RandomPersonnelGridRecord"
import RandomPersonnelGridOptions from "../../api/Randoms/RandomPersonnelModel/RandomPersonnelGridOptions"
import RandomPersonnelIdentifier from "../../api/Randoms/RandomPersonnelModel/RandomPersonnelIdentifier"
import { RandomPersonnelGridObservable, RandomPersonnelExportCsv, RandomPersonnelPrint, RandomPersonnelGridResponse } from "../../api/Randoms/RandomPersonnelListGet"
import RandomPersonnelImport from "../../api/Randoms/RandomPersonnelImport"
import RandomPersonnelRemove from "../../api/Randoms/RandomPersonnelRemove"
import AutoFilterCompany from "../../api/Companies/CompanyModel/AutoFilterCompany"
import AutoFilterLocation from "../../api/Location/LocationModel/AutoFilterLocation"
import AutoFilterRegion from "../../api/Regions/Model/AutoFilterRegion"
import RegulatoryMode from "../../api/LabService/LabServiceModel/RegulatoryMode"  // LabServiceRegulatoryModesGet
import EmployeeCategory from "../../api/LabService/LabServiceModel/EmployeeCategory"
import RandomPersonnelSearchFieldsObservable from "../../api/Randoms/RandomPersonnelSearchFieldsGet"
import RandomPersonnelCheckbox from "./RandomPersonnelCheckbox"
import ColumnsCustomizeModal from "../ColumnsCustomizeModal/ColumnsCustomizeModal"
import EmployeeDetailsModal from "../OrderResults/EmployeeDetailsModal"
import TablePagination from "../TablePagination"
import ColumnHeaderFilter from "../ColumnHeaderFilter"
import SearchBar from "../SearchBar"
import SearchField from "../../api/SearchField"
import AdvancedFiltersModal from "./AdvancedFiltersModal"
import ConfirmationModal from "../ConfirmationModal"
import AddIndividualModal from "./AddIndividualModal"
import FormatSSN from "../../formatters/FormatSSN"
import FormatDate from "../../formatters/FormatDate"
import FormatTimestampFileName from "../../formatters/FormatTimestampFileName"
import ActionButton from "../ActionButton"
import Checkbox from "../Checkbox"
import { CustomizableFilterColumn } from "../ColumnsCustomizeModal/CustomizableColumn"
import { AppPageWrapper } from "../NavMenu/AppPageWrapper"
import { connect } from "react-redux"
import { AppState, AssignedClinic } from "../../AppState"
import RandomNav from "./RandomNav"
import { ColumnFilters } from "../../api/Randoms/RandomPersonnelModel/ColumnFilters"
import { StorageKey } from "../../config/Constants"
import { loadCustomized, saveCustomized } from "../ColumnsCustomizeModal/ColumnsCustomizeModal"
import { BuildUserAccess } from "../../authentication/BuildUserAccess"
import { GetSelectedAssignedClinic, GetSelectedPortal } from "../../authentication/SelectedCompany/CompanySelectionDrivenSelectors"
import ImportErrorDetails from "./ImportErrorDetails"
import { ImportJobValidationResult } from "../../api/Randoms/Import/ImportJobValidationResultGet"
import ImportJobCreateAndPerform from "../../api/Randoms/Import/ImportJobCreateAndPerform"
import ImportErrorDetailsModal from "./ImportErrorDetailsModal"
import ConsortiumMembershipModal from "./ConsortiumMembershipModal"
import { isPortalAdmin } from "../../authentication/isAdminRole"
import { csvHeader } from "../../api/Randoms/ImportPersonnelTemplate"
import GetDonorID, { GetDonorIDTitle } from "../../formatters/RenderDonorID"

type Column = CustomizableFilterColumn<keyof RandomPersonnelGridRecord, keyof ColumnFilters>

interface RandomPersonnelGridProps {
  canEditPersonnel?: boolean
  canManagePools?: boolean
  canManagePersonnel?: boolean
  canManageConsortiumPools?: boolean
  isLocation?: boolean
  isPortalAdmin?: boolean
  isConsortium?: boolean
  currentCompany?: AssignedClinic
  unmaskDonorId?: number
}

interface RandomPersonnelGridStateSelectedPersons {
  selectedPrintRowIDs: RandomPersonnelIdentifier[]
  selectedRemoveRowIDs: RandomPersonnelIdentifier[]
  selectedChangeStatusRowIDs: RandomPersonnelIdentifier[]
}

interface RandomPersonnelGridState extends RandomPersonnelGridStateSelectedPersons {
  fileInputKey: string
  loading: boolean
  loadingCsv: boolean
  importing: boolean
  removing: boolean
  data?: RandomPersonnelGridResponse
  columns: Column[]
  customizeModal: boolean
  advancedFiltersModal: boolean
  removeConfirmModal: boolean
  forceRemoveConfirmModal: boolean
  removePersonMessage: string
  addIndividualModal: boolean
  showConsortiumsModal: boolean
  searchFields?: SearchField[]
  selectedCompanies: AutoFilterCompany[]
  selectedRegions: AutoFilterRegion[]
  selectedLocations: AutoFilterLocation[]
  selectedRegulatoryModes: RegulatoryMode[]
  selectedCategories: EmployeeCategory[]
  employeeDetails: boolean
  details?: RandomPersonnelGridRecord
  importJobID: number
  jobResult: boolean
}

const PageSize = 20
const CSV_FILE_NOT_CORRECT_FORMAT_CODE = "-112"
const SEARCH_FIELD_NAME_SSN = "SSN"

class RandomPersonnelGrid extends React.PureComponent<RandomPersonnelGridProps, RandomPersonnelGridState> {
  defaultColumns: Column[] = [
    { name: "ID", filter: "IDNumber", label: "ID NUMBER", visible: true, customize: false },
    { name: "Name", filter: "Name", label: "NAME", visible: true, customize: true },
    { name: "Company", filter: "Company", label: "COMPANY", visible: true, customize: true },
    { name: "Region", filter: "Region", label: "REGION", visible: this.props.isConsortium ? false : true, customize: this.props.isConsortium ? false : true },
    { name: "Location", filter: "Location", label: "LOCATION", visible: true, customize: true },
    { name: "Reg", filter: "RegulatoryMode", label: "REGULATORY MODE", visible: true, customize: true },
    { name: "Category", filter: "Category", label: "CATEGORY", visible: true, customize: true },
    { name: "Inactive", filter: "Status", label: this.props.isConsortium ? "EMPLOYMENT STATUS" : "STATUS", visible: true, customize: true },
    { name: "ReasonForInactivation", label: "REASON FOR INACTIVATION", visible: true, customize: true },
    { name: "Occupation", filter: "Occupation", label: "OCCUPATION", visible: true, customize: true },
    { name: "Supervisor1", filter: "Supervisor1", label: "SUPERVISOR 1", visible: false, customize: true },
    { name: "Supervisor2", filter: "Supervisor2", label: "SUPERVISOR 2", visible: false, customize: true },
    { name: "HireDate", filter: "HireDate", label: "HIRE DATE", visible: false, customize: true },
    { name: "TerminationDate", filter: "TerminationDate", label: "TERMINATION DATE", visible: false, customize: true },
    { name: "PreEmploymentTestOnFile", filter: "PreEmploymentTestOnFile", label: "PRE-EMPLOYMENT TEST ON FILE", visible: false, customize: true },
    { name: "Gender", filter: "Gender", label: "GENDER", visible: false, customize: true },
    { name: "DateOfBirth", filter: "DateOfBirth", label: "DATE OF BIRTH", visible: false, customize: true },
    { name: "AddressLine1", filter: "AddressLine1", label: "STREET ADDRESS", visible: false, customize: true },
    { name: "City", filter: "City", label: "CITY", visible: false, customize: true },
    { name: "State", filter: "State", label: "STATE", visible: false, customize: true },
    { name: "Zip", filter: "Zip", label: "ZIP", visible: false, customize: true },
    { name: "WorkPhone", filter: "WorkPhone", label: "WORK PHONE", visible: false, customize: true },
    { name: "CellPhone", filter: "CellPhone", label: "MOBILE PHONE", visible: false, customize: true },
    { name: "Email", filter: "Email", label: "EMAIL", visible: false, customize: true },
    { name: "User1", filter: "User1", label: "USER 1", visible: false, customize: true },
    { name: "User2", filter: "User2", label: "USER 2", visible: false, customize: true },
    { name: "User3", filter: "User3", label: "USER 3", visible: false, customize: true },
    { name: "User4", filter: "User4", label: "USER 4", visible: false, customize: true },
    { name: "User5", filter: "User5", label: "USER 5", visible: false, customize: true },
    { name: "User6", filter: "User6", label: "USER 6", visible: false, customize: true },
    { name: "User7", filter: "User7", label: "USER 7", visible: false, customize: true },
    { name: "User8", filter: "User8", label: "USER 8", visible: false, customize: true },
    { name: "User9", filter: "User9", label: "USER 9", visible: false, customize: true },
    { name: "User10", filter: "User10", label: "USER 10", visible: false, customize: true },
  ]

  state: RandomPersonnelGridState = {
    fileInputKey: "file_input_key",
    loading: false,
    loadingCsv: false,
    importing: false,
    removing: false,
    columns: loadCustomized(StorageKey.RandomPersonnelGridColumns, this.defaultColumns) as Column[],
    customizeModal: false,
    advancedFiltersModal: false,
    removeConfirmModal: false,
    forceRemoveConfirmModal: false,
    removePersonMessage: "",
    addIndividualModal: false,
    showConsortiumsModal: false,
    selectedPrintRowIDs: [],
    selectedRemoveRowIDs: [],
    selectedChangeStatusRowIDs: [],
    selectedCompanies: [],
    selectedRegions: [],
    selectedLocations: [],
    selectedRegulatoryModes: [],
    selectedCategories: [],
    employeeDetails: false,
    importJobID: -1,
    jobResult: false
  }
  page$ = new BehaviorSubject<RandomPersonnelGridOptions>({
    search: { field: "", text: "" },
    sort: { column: "", order: "" },
    filters: [],
    columnFilters: [],
    pageNumber: 1,
    pageSize: PageSize,
    isConsortium: !!this.props.isConsortium,
    forceReload: false,
  })
  pageSubscription?: Subscription
  searchFieldsSubscription?: Subscription
  removeSubscription?: Subscription
  importSubscription?: Subscription

  file = React.createRef<HTMLInputElement>()

  componentDidMount() {
    this.pageSubscription = this.page$.pipe(
      switchMap(params => concat(
        of({ loading: true }),
        RandomPersonnelGridObservable(params).pipe(
          map((result: RandomPersonnelGridResponse) => ({ data: result, loading: false })),
          catchError(() => of({ loading: false }))
        )))
    ).subscribe(
      (newState: { loading: boolean, data?: RandomPersonnelGridResponse }) => {
        if (newState.data) {
          if (newState.data.HireDate?.length) {
            newState.data.HireDate = newState.data.HireDate.map((dateString) => FormatDate(dateString))
          }
          if (newState.data.TerminationDate?.length) {
            newState.data.TerminationDate = newState.data.TerminationDate.map((dateString) =>
              FormatDate(dateString)
            )
          }
          if (newState.data.DateOfBirth?.length) {
            newState.data.DateOfBirth = newState.data.DateOfBirth.map((dateString) => FormatDate(dateString))
          }
          this.setState({ loading: newState.loading, data: newState.data, selectedPrintRowIDs: [], selectedRemoveRowIDs: [], selectedChangeStatusRowIDs: [] })
        } else {
          this.setState({ loading: newState.loading })
        }
      },
      (error: string) => {
        this.setState({ loading: false })
        toast.error(error)
      }
    )
    this.searchFieldsSubscription = RandomPersonnelSearchFieldsObservable().subscribe(
      data => this.setState({ searchFields: data.map(value => { return { Name: value.Name, ID: value.Name } }) }),
      error => toast.error(error)
    )
  }

  componentWillUnmount() {
    this.pageSubscription && this.pageSubscription.unsubscribe()
    this.searchFieldsSubscription && this.searchFieldsSubscription.unsubscribe()
    this.removeSubscription && this.removeSubscription.unsubscribe()
    this.importSubscription && this.importSubscription.unsubscribe()
  }

  visibleColumns = () => this.state.columns.filter(col => col.visible)

  renderCell = (row: RandomPersonnelGridRecord, colName: keyof RandomPersonnelGridRecord, key: number) => {
    switch (colName) {
      case "ID":
        return (
          <td key={key} className="align-text-top">
            <Button variant="link" className="p-0" onClick={() => this.showEmployeeDetails(row)}>
              {GetDonorID(row[colName], row["MockID"], this.props.unmaskDonorId ? this.props.unmaskDonorId : 0)}
            </Button>
          </td>
        )
      case "Inactive":
        return (
          <td key={key} className="align-text-top">
            {row[colName] ? "Inactive" : "Active"}
          </td>
        )
      case "DateActive":
      case "HireDate":
      case "TerminationDate":
      case "DateOfBirth":
        return (
          <td key={key} className="align-text-top">
            {FormatDate(row[colName])}
          </td>
        )
      case "PreEmploymentTestOnFile":
        return (
          <td key={key} className="align-text-top">
            {row[colName] ? "Yes" : "No"}
          </td>
        )
      default:
        return (
          <td key={key} className="align-text-top">
            {row[colName]}
          </td>
        )
    }
  }

  sortGrid(column: string, order?: string) {
    this.page$.next({ ...this.page$.value, sort: { column: column, order: order || "" }, forceReload: false })
  }

  filterGridColumn(column: string, items: string[]) {
    const columnFilters = this.page$.value.columnFilters
    const filter = { column: column, values: items }
    let index = columnFilters.findIndex(item => item.column === filter.column)
    if (index >= 0) {
      columnFilters[index] = filter
    } else {
      columnFilters.push(filter)
    }

    this.page$.next({ ...this.page$.value, pageNumber: 1, columnFilters: columnFilters })
  }

  handleExportClick(fileName: string) {
    this.setState({ loadingCsv: true })
    RandomPersonnelExportCsv(this.page$.value).subscribe(
      result => this.export(result, fileName),
      error => {
        toast.error(error)
        this.setState({ loadingCsv: false })
      },
      (() => this.setState({ loadingCsv: false }))
    )
  }

  export(csv: string, fileName: string) {
    const csvData = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    FileSaver.saveAs(csvData, FormatTimestampFileName(fileName, "csv", new Date()), true)
    toast.success("CSV file downloaded successfully.")
  }

  performImport(base64string: string, fileName: string = "") {
    this.setState({ importing: true })
    this.importSubscription && this.importSubscription.unsubscribe()
    this.importSubscription = ImportJobCreateAndPerform(base64string, true, fileName, this.props.currentCompany?.AccountID, 0, false, true).subscribe(
      result => {
        this.setState({ importing: false, importJobID: result.ImportJobID, jobResult: result.Result})
        if (result.Result) {
          this.refreshGrid()
          toast.success("File uploaded successfully.")
        }
      },
      error => {
        this.setState({ importing: false })
        if (error.errorData?.code !== CSV_FILE_NOT_CORRECT_FORMAT_CODE) {
          this.refreshGrid()
        }
        toast.error(error.errorData?.message || error.message)
      }
    )

  }

  handleRemoveClick() {
    this.setState({ removeConfirmModal: true })
  }

  performRemove = (force: boolean = false) => {
    this.setState({ removeConfirmModal: false, removing: true })
    this.removeSubscription && this.removeSubscription.unsubscribe()
    this.removeSubscription = RandomPersonnelRemove(this.state.selectedRemoveRowIDs, force).subscribe(
      data => {
        this.setState({ removing: false, forceRemoveConfirmModal: false, selectedRemoveRowIDs: [] })
        this.refreshGrid(true)
        toast.success(data)
      },
      error => {
        if (error.message == "Conflict") {
          this.setState({ removing: false, forceRemoveConfirmModal: true, removePersonMessage: error.errorData?.message })
        } else {
          this.setState({ removing: false, forceRemoveConfirmModal: false })
          toast.error(error.errorData?.message || error.message)
        }
      }
    )
  }

  handlePrintClick() {
    RandomPersonnelPrint(this.state.selectedPrintRowIDs, this.page$.value).subscribe(
      data => !!data ? this.print(data) : toast.error("Data not found."),
      error => toast.error(error)
    )
  }

  print(base64pdf: string) {
    const pdfWindow = window.open("")
    if (pdfWindow) {
      pdfWindow.document.write("<iframe width='100%' height='100%' src='data:application/pdf;base64, " + encodeURI(base64pdf) + "'></iframe>")
    }
  }

  handlePrintSelectAllRowsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedPrintRowIDs = e.target.checked && this.state.data && this.state.data.Records ? this.state.data.Records.map((row: RandomPersonnelGridRecord, i: number) => ({ PersonID: row.PersonID, ID: row.ID })) : []
    this.setState({ selectedPrintRowIDs: selectedPrintRowIDs })
  }

  changePersonRowSelection = (person: RandomPersonnelIdentifier, checked: boolean, stateFieldName: keyof RandomPersonnelGridStateSelectedPersons) => {
    let selectedRows = [...this.state[stateFieldName]]

    const index = selectedRows.findIndex(item => item.PersonID === person.PersonID && item.ID === person.ID)
    if (index > -1) {
      selectedRows = [...selectedRows.slice(0, index), ...selectedRows.slice(index + 1)]
    } else {
      selectedRows.push(person)
    }
    this.setState({ ...{}, [stateFieldName]: selectedRows })
  }

  handlePrintSelectRowChange = (person: RandomPersonnelIdentifier, checked: boolean) => {
    this.changePersonRowSelection(person, checked, "selectedPrintRowIDs")
  }

  handleRemoveSelectRowChange = (person: RandomPersonnelIdentifier, checked: boolean) => {
    this.changePersonRowSelection(person, checked, "selectedRemoveRowIDs")
  }

  handleActivateInactivateClick = (data: RandomPersonnelGridRecord) => this.setState({showConsortiumsModal: true, details: data})

  handleChangePage(pageNumber: number) {
    this.page$.next({ ...this.page$.value, pageNumber: pageNumber })
  }

  handleSearch = (field: string, text: string) => {
    if (field === SEARCH_FIELD_NAME_SSN) {
      text = text.replace(/\D/g, "")
    }
    this.page$.next({ ...this.page$.value, search: { field: field, text: text } })
  }

  handleAdvancedFiltersSave = (companies: AutoFilterCompany[], regions: AutoFilterRegion[], locations: AutoFilterLocation[], regulatoryModes: RegulatoryMode[], categories: EmployeeCategory[], closeModal: boolean) => {
    this.setState({
      selectedCompanies: companies,
      selectedRegions: regions,
      selectedLocations: locations,
      selectedRegulatoryModes: regulatoryModes,
      selectedCategories: categories,
      advancedFiltersModal: !closeModal
    })
    const filters = [
      { column: "Company", values: companies.map((item) => item.Name) },
      { column: "Region", values: regions.map((item) => item.Name) },
      { column: "Location", values: locations.map((item) => item.Name) },
      { column: "RegulatoryMode", values: regulatoryModes.map((item) => item.Name) },
      { column: "Category", values: categories.map((item) => item.Name) },
    ]
    this.page$.next({ ...this.page$.value, filters: filters })
  }

  handleAddIndividualSave = () => {
    this.setState({ addIndividualModal: false })
    this.refreshGrid()
  }

  handleHandleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target && e.target.files && e.target.files[0]
    if (file) {
      const ext = file.name.substring(file.name.lastIndexOf('.') + 1)
      if (ext && ext.toLowerCase() === "csv") {
        var reader = new FileReader()
        const self = this
        reader.onload = function (e) {
          const result = e.target?.result as string || ""
          const stringLength = result.indexOf('base64') + 7
          self.performImport(result.slice(stringLength, result.length), file.name)
        }
        reader.onerror = function (error) { toast.error(error) }
        reader.readAsDataURL(file)
      } else {
        toast.error("File type not allowed, Please upload CSV file.")
      }
    }
    this.setState({ fileInputKey: Math.random().toString() })
  }

  downloadTemplate = () => {
    // const header = this.props.isPortalAdmin ? csvHeader : csvHeader.filter((item) => item.toUpperCase() !== "MANAGER")
    const csvData = new Blob([csvHeader.join(",") + "\r\n"], { type: 'text/csv;charset=utf-8;' })
    FileSaver.saveAs(csvData, "import-list-template.csv")
  }

  formatSearchString = (ID: string, value: string): string => {
    return ID === SEARCH_FIELD_NAME_SSN ? FormatSSN(value) : value
  }

  showEmployeeDetails = (data: RandomPersonnelGridRecord) => this.setState({ employeeDetails: true, details: data })

  refreshGrid = (forceReload: boolean = false ) => this.page$.next({ ...this.page$.value, forceReload: forceReload })

  render() {
    const isConsortium = !!this.props.isConsortium
    const headerColumns = this.visibleColumns().map((col: Column, i) => {
      const sort = this.page$.value.sort
      const filterValues = this.page$.value.filters.find(filter => filter.column === col.name)?.values || []
      // Removed Advanced Filters button due to decision made 05/28/2021 on SCRUM.
      //
      const message = filterValues.length > 0 ?
         (<>
           <div>Some options in this list may be hidden</div>
           <div>because of the advanced filter configuration.</div>
           <div>Click <span className="text-link" onClick={() => this.setState({ advancedFiltersModal: true })}>filters</span> or ADVANCED FILTERS button to configure</div>
         </>) :
         <></>
      const columnFilterValues = this.page$.value.columnFilters.find(filter => filter.column === col.filter)?.values || []
      return (
        <th key={col.name}>
          {this.state.data && col.filter && col.filter in this.state.data && <ColumnHeaderFilter title={col.label || col.name}
            filtered={columnFilterValues.length > 0}
            filteredItems={columnFilterValues}
            sort={sort && sort.column === col.name ? sort.order === "asc" ? "asc" : "desc" : undefined}
            items={this.state.data[col.filter as keyof ColumnFilters] || []}
            // message={message}
            onSort={(order?: string) => this.sortGrid(col.name, order)}
            onFilter={(items: string[]) => col.filter && this.filterGridColumn(col.filter, items)}
          />}
          {(!this.state.data || !col.filter) && (col.label || col.name)}
        </th>
      )
    })
    const allPrintChecked = !this.state.loading && !!this.state.data?.Records?.length && this.state.data?.Records?.length === this.state.selectedPrintRowIDs.length
    return (<AppPageWrapper parentClassName="align-items-center" showNav>
      <RandomNav canManagePools={this.props.canManagePools && !this.props.isLocation} canManagePersonnel={this.props.canManagePersonnel} canManageConsortiumPools={this.props.canManageConsortiumPools} isConsortium={!!this.props.isConsortium} />
      { this.state.employeeDetails && this.state.details && <EmployeeDetailsModal
        personID={this.state.details.PersonID}
        name={this.state.details.Name}
        id={this.state.details.SSN}
        onHide={(updated) => this.setState({ employeeDetails: false }, () => updated && this.refreshGrid())} />}
      <ColumnsCustomizeModal show={this.state.customizeModal}
        columns={this.state.columns}
        storeKey={StorageKey.RandomPersonnelGridColumns}
        onClose={() => this.setState({ customizeModal: false })}
        onSave={(columns) => this.setState({ columns: columns as Column[], customizeModal: false })} />
      {/*
        Removed Advanced Filters button due to decision made 05/28/2021 on SCRUM.
        "d) Pass Column filters values via "Filters" parameter,
        remove Advanced Filters button from the Personnel grid and address this issue later in future releases.
        Change CSV Export and Print to work without filters (pass empty filters to requests)"
      */
      <AdvancedFiltersModal show={this.state.advancedFiltersModal}
        selectedCompanies={this.state.selectedCompanies}
        selectedRegions={this.state.selectedRegions}
        selectedLocations={this.state.selectedLocations}
        selectedRegulatoryModes={this.state.selectedRegulatoryModes}
        selectedCategories={this.state.selectedCategories}
        accountType=""
        onClose={() => this.setState({ advancedFiltersModal: false })}
        onSave={this.handleAdvancedFiltersSave} /> }
      {<AddIndividualModal show={this.state.addIndividualModal}
        onCancel={() => this.setState({ addIndividualModal: false })}
        onAdd={this.handleAddIndividualSave} />}
      <ConfirmationModal show={this.state.removeConfirmModal}
        title="CONFIRM REMOVE"
        textTitle="Are you sure you want to remove selected record?"
        text="All the selected records will be removed permanently."
        onConfirm={() => this.performRemove()}
        onHide={() => this.setState({ removeConfirmModal: false })} />
      <ConfirmationModal show={this.state.forceRemoveConfirmModal}
        title="CONFIRM REMOVE"
        textTitle="Please confirm force remove selected personnel"
        text={this.state.removePersonMessage}
        onConfirm={() => this.performRemove(true)}
        onHide={() => this.setState({ forceRemoveConfirmModal: false })} />
      {this.state.showConsortiumsModal && this.state.details && <ConsortiumMembershipModal PersonID={this.state.details.PersonID} onClose={() => this.setState({showConsortiumsModal: false, details: undefined})}/>}
      <Container fluid className="grid-container">
        <Row className="pb-2">
          <Col>
            <SearchBar onSearch={this.handleSearch} searchFields={this.state.searchFields || []} formatValue={this.formatSearchString} placeholder="All Personnel IDs" />
          </Col>
          <Col lg={7} className="text-right">
            <Button variant="secondary" size="sm" className="mr-1" onClick={() => this.setState({ customizeModal: true })}>CUSTOMIZE</Button>
            {this.props.canEditPersonnel && !isConsortium && !this.props.isPortalAdmin && <Button variant="secondary" size="sm" className="mr-1" onClick={() => this.setState({ addIndividualModal: true })}>ADD INDIVIDUAL</Button>}
            {/*
              Removed Advanced Filters button due to decision made 05/28/2021 on SCRUM.
              "d) Pass Column filters values via "Filters" parameter,
              remove Advanced Filters button from the Personnel grid and address this issue later in future releases.
              Change CSV Export and Print to work without filters (pass empty filters to requests)"
            */
              <Button variant="secondary" size="sm" className="mr-1" onClick={() => this.setState({ advancedFiltersModal: true })}>ADVANCED FILTERS</Button>
            }
            {this.props.canEditPersonnel && !this.props.isLocation && <Button variant="secondary" size="sm" className="mr-1" onClick={() => this.downloadTemplate()}>DOWNLOAD TEMPLATE</Button>}
            {this.props.canEditPersonnel && !isConsortium && !this.props.isLocation && !this.props.isPortalAdmin && <ActionButton variant="secondary" size="sm" className="mr-1" onClick={() => { this.file?.current?.click() }} spin={this.state.importing} disableOnSpin>IMPORT LIST</ActionButton>}
            <input type="file" key={this.state.fileInputKey} ref={this.file} accept=".csv" onChange={this.handleHandleFileUpload} style={{ display: 'none' }} />
            {!isConsortium && <ActionButton variant="secondary" size="sm" onClick={() => this.handleExportClick("Personnel")} disabled={!this.state.data?.Records?.length} spin={this.state.loadingCsv} disableOnSpin>EXPORT CSV</ActionButton>}
          </Col>
        </Row>
        <Row>
          <Col>
            <Table bordered responsive>
              <thead>
                <tr>
                  {!isConsortium && <th className="text-center print-column">
                    <Checkbox id="print_all_personnel" checked={allPrintChecked} onChange={this.handlePrintSelectAllRowsChange} />
                    <Button variant="secondary" size="sm" onClick={() => this.handlePrintClick()} className="float-right" disabled={this.state.selectedPrintRowIDs.length === 0}><FaPrint /></Button>
                  </th>}
                  {headerColumns}
                  {this.props.canEditPersonnel && !isConsortium && <th className="text-center small-column">
                    <Button variant="secondary" size="sm" onClick={() => this.handleRemoveClick()} disabled={this.state.selectedRemoveRowIDs.length === 0}>Remove</Button>
                  </th>}
                  {isConsortium && <th className="text-center small-column">Consortiums Member</th>}
                </tr>
              </thead>
              <tbody>
                {this.state.loading && <tr><td colSpan={this.visibleColumns().length + (this.props.canEditPersonnel ? 2 : 1)}><Spinner animation="border" variant="secondary" /></td></tr>}
                {!this.state.loading && (this.state.data && this.state.data.Records && this.state.data.Records.length > 0
                  ? this.state.data.Records.map((row: RandomPersonnelGridRecord, i: number) => {
                    const person = { PersonID: row.PersonID, ID: row.ID }
                    return (
                      <tr key={`${person.PersonID}_${person.ID}_1_${i}`}>
                        {!isConsortium && <td className="text-center">
                          <RandomPersonnelCheckbox
                            id={`${person.PersonID}_${person.ID}_check1_${i}`}
                            person={person}
                            checked={!!this.state.selectedPrintRowIDs.find(item => person.PersonID === item.PersonID && person.ID === item.ID)}
                            onChange={this.handlePrintSelectRowChange} />
                        </td>}
                        {this.visibleColumns().map((col, i) => this.renderCell(row, col.name as keyof RandomPersonnelGridRecord, i))}
                        { this.props.canEditPersonnel && !isConsortium && <td className="text-center">
                          <RandomPersonnelCheckbox
                            id={`${person.PersonID}_${person.ID}_check2_${i}`}
                            person={person}
                            checked={!!this.state.selectedRemoveRowIDs.find(item => person.PersonID === item.PersonID && person.ID === item.ID)}
                            onChange={this.handleRemoveSelectRowChange} />
                        </td>}
                        {isConsortium && <td className="text-center">
                          <Button variant="secondary" size="sm" onClick={() => this.handleActivateInactivateClick(row)}>Activate/Inactivate</Button>
                        </td>}
                      </tr>)
                  })
                  : <tr><td colSpan={this.visibleColumns().length + (this.props.canEditPersonnel || isConsortium ? 2 : 1)}>NO DATA FOUND</td></tr>
                )}
              </tbody>
            </Table>
            {this.state.data && !this.state.loading && <span className="total-records">Total records: {this.state.data.Total}</span>}
            {this.state.data && <TablePagination pageNumber={this.page$.value.pageNumber} pages={this.state.data.Pages} onChange={pageNumber => this.handleChangePage(pageNumber)} />}
          </Col>
        </Row>
      </Container>
      {!this.state.importing &&
        <ImportErrorDetailsModal
          show={this.state.importJobID > 0 && !this.state.jobResult}
          importJobID={this.state.importJobID}
          onClose={() => this.setState({ importJobID: -1 })} />}
    </AppPageWrapper>)
  }
}
function mapStateToProps(state: AppState): RandomPersonnelGridProps {
  const selectedCompany =  GetSelectedAssignedClinic(state)
  const userAccess = BuildUserAccess(state)
  const portal = GetSelectedPortal(state)
  const unmaskDonorId = portal ? portal.UnmaskDonorID : 0

  return {
    canEditPersonnel: userAccess.canEditPersonnel,
    canManagePools: userAccess.canManagePools,
    canManagePersonnel: userAccess.canManagePersonnel,
    canManageConsortiumPools: userAccess.CanManageConsortiumPools,
    isLocation: (GetSelectedAssignedClinic(state)?.LocationID || 0) > 0,
    currentCompany: selectedCompany || undefined,
    isPortalAdmin: isPortalAdmin(state),
    unmaskDonorId: unmaskDonorId
  }
}
const ConnectedRandomPersonnelGrid = connect(mapStateToProps, null)(RandomPersonnelGrid)
export default ConnectedRandomPersonnelGrid
